/**
 * Clinic staff: who has access, what they can reach, and adding more.
 *
 * ONE CARD PER PERSON, not per grant. The list was one row per grant, so
 * somebody holding a clinic-wide role and two store roles appeared three times
 * with the caption apologising for it. A person is the subject here; their
 * grants are an attribute of them, and the card carries all of them.
 *
 * Everything about a member is decided from this screen. It used to be a table
 * that linked away plus a permanently-open form at the bottom of the page: the
 * add form was the loudest thing on screen even when nobody was adding anybody,
 * and changing a permission meant leaving the list. Adding is a dialog from the
 * header; reading and changing happen in a sheet over the list.
 *
 * The shape follows the JMC user screen, which follows how account lists work
 * where this problem is already solved (Okta, Google Workspace Admin, Auth0): a
 * search box and role chips carrying the count of what is behind them, one row
 * per person, and actions that open over the list rather than navigating away. A
 * clinic with thirty staff scans a table; it cannot scan thirty cards.
 *
 * Adding creates the account immediately - there is no invitation to send. The
 * person can only use it by signing in as that verified address, so the grant
 * records what the email may reach, not who it is.
 */
import { ROLE_LABEL, ROLE_REACH, roleOptions, sessionRole, type TenantRoleName } from '../roles.ts';
import { useMemo, useState } from 'react';
import { useLoaderData, useOutletContext } from 'react-router';
import {
  del,
  imageUrl,
  patch,
  post,
  type ListResponse,
  type Member,
  type Session,
} from '../api.ts';
import {
  Card,
  Empty,
  Pill,
  RowMenu,
  StatusPill,
  Table,
  jaDate,
  jaNumber,
  rowOpen,
} from '../components/ui.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { StoreChecklist } from '../components/StoreChecklist.tsx';
import { PASSWORD_HINT, MIN_PASSWORD_LENGTH } from '../../../shared/password-policy.ts';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonRows, usePendingScreen } from '../components/Skeleton.tsx';


/** One person, with every grant they hold in this clinic. */
interface Person {
  userId: string;
  displayName: string;
  email: string | null;
  avatarId: string | null;
  lastLoginAt: string | null;
  accountStatus: string;
  claimed: boolean;
  grants: Member[];
}

/**
 * The rows, folded into people.
 *
 * The API returns a grant per row because that is what the tables hold, and it
 * is the right shape for a permission screen and the wrong one for a list of
 * staff. Folded here rather than server-side: the same endpoint answers both
 * questions, and the grouping is four lines.
 */
function peopleOf(items: Member[]): Person[] {
  const byUser = new Map<string, Person>();
  for (const row of items) {
    const person = byUser.get(row.userId);
    if (person) {
      person.grants.push(row);
      continue;
    }
    byUser.set(row.userId, {
      userId: row.userId,
      displayName: row.displayName,
      email: row.email,
      avatarId: row.avatarId ?? null,
      lastLoginAt: row.lastLoginAt ?? null,
      accountStatus: row.accountStatus ?? 'ACTIVE',
      claimed: row.claimed ?? true,
      grants: [row],
    });
  }
  return [...byUser.values()];
}

/**
 * The grants, read as the one thing they describe.
 *
 * A person belongs to this clinic and is either its admin or covers some of its
 * branches. The tables hold a row per grant because that is what a grant is, and
 * editing them AS rows is what produced the mess: promoting a store user meant
 * adding a clinic-wide grant and remembering to revoke the store one, so people
 * ended up holding both and the list had to explain itself. This is the same
 * reading the JMC screen takes (AdminUserDetail, migration 074).
 */
function affiliationOf(person: Person): {
  clinicAdmin: boolean;
  /** The one role this person holds here, widest first. */
  role: TenantRoleName;
  storeIds: string[];
  suspended: boolean;
} {
  const active = person.grants.filter((grant) => grant.status === 'ACTIVE');
  const clinicAdmin = active.some((grant) => grant.storeId === null);
  return {
    clinicAdmin,
    role: clinicAdmin
      ? 'CLINIC_ADMIN'
      : active.some((grant) => grant.role === 'STORE_ADMIN')
        ? 'STORE_ADMIN'
        : 'STORE_USER',
    storeIds: active
      .filter((grant) => grant.storeId !== null)
      .map((grant) => grant.storeId!),
    // Every grant switched off: they are still in the clinic and can reach none
    // of it, which is a state the row has to show rather than look empty.
    suspended: active.length === 0,
  };
}

/** The reach of a grant, as the one phrase that identifies it. */
const grantLabel = (grant: Member): string =>
  grant.storeId === null
    ? 'クリニック全体'
    : (grant.storeName ?? '店舗');

/**
 * One person, as a row.
 *
 * The face beside the name, so a list of people reads as people rather than as
 * rows of text - the initial stands in when there is no picture, never a
 * silhouette, which is the same for everybody and therefore says nothing. Every
 * grant is a chip in one cell: this is the answer to "what can they reach",
 * which is what a staff list is read for, and it was a second row per grant.
 */
function PersonRow({
  person,
  index,
  onOpen,
  onEditAccess,
  onProfile,
  isSelf,
}: {
  person: Person;
  index: number;
  onOpen: () => void;
  onEditAccess: () => void;
  onProfile: () => void;
  isSelf: boolean;
}) {
  const name = person.displayName || person.email || '（名称なし）';
  const reach = affiliationOf(person);

  return (
    <m.tr key={person.userId} data-motion layout {...listItem(index)} {...rowOpen(onOpen)}>
      <td>
        <span className="rowname rowname--person">
          <span className="rowavatar" aria-hidden>
            {person.avatarId ? (
              <img src={imageUrl(person.avatarId)} alt="" width={28} height={28} />
            ) : (
              name.trim().charAt(0).toUpperCase()
            )}
          </span>
          <span>{name}</span>
        </span>
      </td>
      <td className="muted">{person.email ?? '—'}</td>
      <td>
        {/*
          What they can reach, as one answer. It was a chip per grant, which
          showed 「クリニック管理」 beside 「店舗ユーザー・渋谷院」 for the same person -
          the duplicate the old edit flow created, presented as though it meant
          something.
        */}
        {reach.clinicAdmin ? (
          <Pill>{ROLE_LABEL.CLINIC_ADMIN}（全店舗）</Pill>
        ) : reach.storeIds.length === 0 ? (
          <span className="muted small">担当なし</span>
        ) : (
          <span className="chips">
            {reach.role === 'STORE_ADMIN' && <Pill>{ROLE_LABEL.STORE_ADMIN}</Pill>}
            {person.grants
              .filter((grant) => grant.status === 'ACTIVE' && grant.storeId !== null)
              .map((grant) => (
                <span key={grant.grantId} className="tag">
                  {grantLabel(grant)}
                </span>
              ))}
          </span>
        )}
      </td>
      <td>
        <span className="chips">
          {person.claimed ? (
            <Pill tone="ok">ログイン済み</Pill>
          ) : (
            <Pill tone="warn">未ログイン</Pill>
          )}
          {reach.suspended && <Pill tone="danger">停止中</Pill>}
        </span>
      </td>
      <td className="muted">{person.claimed ? jaDate(person.lastLoginAt) : '—'}</td>
      <td>
        <div className="rowactions">
          <RowMenu>
            <button type="button" onClick={onOpen}>
              権限を確認
            </button>
            <button type="button" onClick={onEditAccess} disabled={isSelf}>
              権限を変更
            </button>
            <button type="button" onClick={onProfile}>
              プロフィールを編集
            </button>
          </RowMenu>
        </div>
      </td>
    </m.tr>
  );
}

export default function Members() {
  const { items } = useLoaderData<ListResponse<Member>>();
  const session = useOutletContext<Session>();
  const stores =
    session.memberships.find((m) => m.clinicId === session.activeClinicId)?.stores ?? [];
  /*
   * Who is managing. A clinic admin grants any role in any store; a store admin
   * grants staff or store admin, in the stores they run - the server and the
   * database refuse anything else, and the dialogs do not offer it.
   */
  const { role: myRole, manageStores } = sessionRole(session);
  const canGrantClinicAdmin = myRole === 'CLINIC_ADMIN';
  const pending = usePendingScreen();
  const people = peopleOf(items);

  /** The person being read. Their grants are edited from inside this sheet. */
  const [viewing, setViewing] = useState<Person | null>(null);
  /** null closed; 'new' is adding a person; a Member is one grant being changed. */
  const [open, setOpen] = useState<'new' | 'access' | 'profile' | null>(null);

  const [invite, setInvite] = useState({
    email: '',
    displayName: '',
    role: 'STORE_USER' as TenantRoleName,
    storeId: manageStores[0]?.id ?? '',
    password: '',
  });
  /*
   * What the person SHOULD have, not what to change.
   *
   * One editor for the whole affiliation, the way the JMC screen does it: the
   * role and the branches are not independent, so editing them as separate
   * grants is what left people holding a clinic-wide role and a store role at
   * once. `suspend` is part of the same picture - taking every scope away is a
   * state of the affiliation, not a fourth kind of grant.
   */
  const [access, setAccess] = useState({
    role: 'STORE_USER' as TenantRoleName,
    storeIds: [] as string[],
    suspend: false,
  });
  const [profileForm, setProfileForm] = useState({ displayName: '', email: '' });
  const [added, setAdded] = useState<string | null>(null);
  /** The toolbar. Plain state, not the URL: a staff list is not a link people share. */
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('');
  const [storeFilter, setStoreFilter] = useState('');

  function startAccess(person: Person) {
    const reach = affiliationOf(person);
    setViewing(person);
    setAccess({
      role: reach.role,
      // Only the stores this manager may assign; the others are kept server-side.
      storeIds: reach.storeIds.filter((id) => manageStores.some((store) => store.id === id)),
      suspend: reach.suspended,
    });
    setOpen('access');
  }

  function startProfile(person: Person) {
    setProfileForm({ displayName: person.displayName, email: person.email ?? '' });
    setOpen('profile');
  }

  const addMember = async () => {
    const created = await post<{ created: boolean }>('/members', {
      email: invite.email,
      // Only used when this creates the account; an existing person's name is
      // theirs. The JMC screens have always asked, and this one did not - so a
      // clinic's staff arrived named after the front of their address.
      ...(invite.displayName.trim() ? { displayName: invite.displayName.trim() } : {}),
      role: invite.role,
      ...(invite.role !== 'CLINIC_ADMIN' ? { storeId: invite.storeId } : {}),
      // Omitted when blank: the schema's minimum would refuse '', and blank is
      // the normal answer.
      ...(invite.password ? { password: invite.password } : {}),
    });
    setAdded(
      created.created
        ? 'アカウントを作成しました。本人がこのメールアドレスでログインすると利用できます。'
        : '既存アカウントに権限を付与しました。',
    );
    setInvite({ ...invite, email: '', displayName: '', password: '' });
  };

  /**
   * Write the whole affiliation.
   *
   * Suspension is a separate call because it is a separate question: 「この人は
   * 何を扱えるか」 and 「いまその権限を使えるか」 are decided independently, and the
   * affiliation endpoint sets the shape while DELETE takes every scope away. The
   * order matters - shape first, then switch it off - so that re-enabling later
   * restores what was chosen here.
   */
  const saveAccess = async () => {
    const target = viewing!;
    if (!access.suspend) {
      await patch(`/members/${target.userId}/affiliation`, {
        role: access.role,
        ...(access.role === 'CLINIC_ADMIN' ? {} : { storeIds: access.storeIds }),
      });
      return;
    }
    // Every scope off. The affiliation is left as it is, so turning it back on
    // is one press rather than a re-selection of branches.
    await del(`/members/${target.userId}`);
  };

  const saveProfile = () =>
    patch(`/members/${viewing!.userId}/profile`, {
      displayName: profileForm.displayName,
      // An unchanged address on a claimed account is refused, and refusing an
      // edit nobody made is not helpful.
      ...(viewing!.claimed ? {} : { email: profileForm.email }),
    });

  // The sheet is rebuilt from the freshly-loaded list, so a change made inside
  // it is visible without closing it.
  const shown = viewing ? (people.find((p) => p.userId === viewing.userId) ?? viewing) : null;
  // Derived beside `shown`, so the sheet reads one affiliation rather than
  // re-deriving it in three places.
  const shownReach = shown
    ? affiliationOf(shown)
    : { clinicAdmin: false, role: 'STORE_USER' as TenantRoleName, storeIds: [], suspended: false };

  /*
   * Search and filters over what is already loaded.
   *
   * Not the JMC screen's server-side filtering, and deliberately: that list is
   * every user of every clinic, where a client-side filter over a full download
   * would be a data leak wearing a convenience costume. This one is a single
   * clinic's own staff - every row is already theirs to see, the loader takes
   * 100 of them, and a round trip per keystroke would buy nothing.
   */
  const matched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return people.filter((person) => {
      /*
       * What they hold NOW.
       *
       * A demotion leaves the old grant on the row as DISABLED - that is what
       * makes re-enabling somebody restore what was chosen - so counting rows
       * rather than live ones showed a store user under 「クリニック管理」 because
       * they had been one last month.
       */
      const reach = affiliationOf(person);
      if (role === 'CLINIC_ADMIN' && reach.role !== 'CLINIC_ADMIN') return false;
      if (role === 'STORE_ADMIN' && reach.role !== 'STORE_ADMIN') return false;
      if (role === 'STORE_USER' && (reach.role !== 'STORE_USER' || reach.storeIds.length === 0)) return false;
      if (role === 'PENDING' && person.claimed) return false;
      if (role === 'DISABLED' && !reach.suspended) return false;
      if (storeFilter && !reach.storeIds.includes(storeFilter)) return false;
      if (needle === '') return true;
      return (
        person.displayName.toLowerCase().includes(needle) ||
        (person.email ?? '').toLowerCase().includes(needle)
      );
    });
  }, [people, query, role, storeFilter]);

  /** The count behind each chip, over every person rather than the filtered set. */
  const facet = (key: string) =>
    people.filter((person) => {
      const reach = affiliationOf(person);
      if (key === 'CLINIC_ADMIN') return reach.role === 'CLINIC_ADMIN';
      if (key === 'STORE_ADMIN') return reach.role === 'STORE_ADMIN';
      if (key === 'STORE_USER') return reach.role === 'STORE_USER' && reach.storeIds.length > 0;
      if (key === 'PENDING') return !person.claimed;
      if (key === 'DISABLED') return reach.suspended;
      return true;
    }).length;

  return (
    <Card
      title="ユーザー管理"
      caption="このクリニックを使える人と、その人が扱える範囲です。行を選ぶと権限を確認・変更できます。"
      actions={
        <button
          className="btn btn--sm"
          type="button"
          onClick={() => {
            setAdded(null);
            setInvite({ ...invite, email: '' });
            setOpen('new');
          }}
        >
          ＋ ユーザーを追加
        </button>
      }
    >
      <div className="tenantbar">
        <div className="field" style={{ marginBottom: 0, minWidth: 220 }}>
          <label htmlFor="mb-q">検索</label>
          <input
            id="mb-q"
            value={query}
            placeholder="氏名またはメールアドレス"
            maxLength={120}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Only when there is a choice to make: one store is not a filter. */}
        {stores.length > 1 && (
          <div className="field" style={{ marginBottom: 0, minWidth: 200 }}>
            <label htmlFor="mb-store-filter">店舗</label>
            <Select
              id="mb-store-filter"
              value={storeFilter}
              onChange={setStoreFilter}
              options={[
                { value: '', label: 'すべて' },
                ...stores.map((store) => ({ value: store.id, label: store.name })),
              ]}
            />
          </div>
        )}
      </div>

      {/*
        Role and state as chips with counts. 未ログイン 3 is both the filter and
        the answer to "has anybody I added never signed in", which is the thing a
        clinic admin actually chases - and a closed dropdown can never be.
      */}
      <div className="chips tenantchips">
        <button
          type="button"
          className="chip"
          aria-pressed={role === ''}
          onClick={() => setRole('')}
        >
          すべて（{jaNumber(people.length)}）
        </button>
        {(
          [
            ...(canGrantClinicAdmin ? ([['CLINIC_ADMIN', ROLE_LABEL.CLINIC_ADMIN]] as const) : []),
            ['STORE_ADMIN', ROLE_LABEL.STORE_ADMIN],
            ['STORE_USER', ROLE_LABEL.STORE_USER],
            ['PENDING', '未ログイン'],
            ['DISABLED', '停止中'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className="chip"
            aria-pressed={role === key}
            onClick={() => setRole(role === key ? '' : key)}
          >
            {label}（{jaNumber(facet(key))}）
          </button>
        ))}
      </div>

      {pending ? (
        <SkeletonRows rows={4} columns={6} />
      ) : matched.length === 0 ? (
        <Empty>
          {people.length === 0
            ? 'ユーザーがまだいません。「＋ ユーザーを追加」から追加できます。'
            : '条件に一致するユーザーがいません。'}
        </Empty>
      ) : (
        <Table
          head={
            <>
              <th scope="col">氏名</th>
              <th scope="col">メールアドレス</th>
              <th scope="col">権限</th>
              <th scope="col">状態</th>
              <th scope="col">最終ログイン</th>
              <th scope="col">操作</th>
            </>
          }
        >
          {/*
            Default mode, not popLayout: popLayout lifts a leaving item out with
            position:absolute, and a table row taken out of its table loses its
            column widths and squashes. Here it fades where it is, then the rows
            below glide up (listItem's layout spring).
          */}
          <AnimatePresence>
            {matched.map((person, index) => (
              <PersonRow
                key={person.userId}
                person={person}
                index={index}
                isSelf={person.userId === session.user.id}
                onOpen={() => setViewing(person)}
                onEditAccess={() => startAccess(person)}
                onProfile={() => startProfile(person)}
              />
            ))}
          </AnimatePresence>
        </Table>
      )}

      {/* The person, and everything decided about them. */}
      <Modal
        open={shown !== null}
        onClose={() => setViewing(null)}
        title={shown?.displayName || shown?.email || 'ユーザー'}
        caption="このクリニックでの権限です。権限ごとに範囲と状態を変えられます。"
        wide
      >
        {shown && (
          <>
            <div className="psheet__head">
              <span className="psheet__face">
                {shown.avatarId ? (
                  <img src={imageUrl(shown.avatarId)} alt="" width={96} height={96} />
                ) : (
                  <span className="ptile__blank" aria-hidden>
                    {(shown.displayName || shown.email || '？').charAt(0)}
                  </span>
                )}
              </span>
              <div>
                <p className="psheet__label">{shown.displayName || '（表示名なし）'}</p>
                <p className="muted small" style={{ margin: '0 0 8px' }}>
                  {shown.email ?? '—'}
                </p>
                <div className="chips">
                  {shown.claimed ? (
                    <Pill tone="ok">ログイン済み</Pill>
                  ) : (
                    <Pill tone="warn">未ログイン</Pill>
                  )}
                  <StatusPill status={shown.accountStatus} />
                  <span className="muted small">
                    {shown.claimed
                      ? `最終ログイン ${jaDate(shown.lastLoginAt)}`
                      : 'このアドレスでログインした人がこのアカウントを引き継ぎます'}
                  </span>
                </div>
              </div>
            </div>

            <p className="fieldgroup">
              <span className="fieldgroup__title">扱える範囲</span>
              <span className="fieldgroup__note">
                この人がこのクリニックで開ける記事と情報です
              </span>
            </p>

            {/*
              One affiliation, not a list of grant rows.

              A person is either this clinic's admin or covers some of its
              branches; the rows underneath are how that is stored. Shown as rows
              it invited editing them as rows, which is what left people holding a
              clinic-wide role AND a store role at the same time.
            */}
            <div className="grants">
              <div className="grants__item">
                <div className="grants__head">
                  <span className="grants__role">{ROLE_LABEL[shownReach.role]}</span>
                  {shownReach.clinicAdmin ? (
                    <Pill>全店舗</Pill>
                  ) : shownReach.storeIds.length === 0 ? (
                    <Pill tone="warn">担当店舗なし</Pill>
                  ) : (
                    shownReach.storeIds.map((storeId: string) => (
                      <Pill key={storeId} tone="muted">
                        {stores.find((store) => store.id === storeId)?.name ?? '店舗'}
                      </Pill>
                    ))
                  )}
                  {shownReach.suspended && <Pill tone="danger">停止中</Pill>}
                </div>
                <p className="grants__reach muted small">
                  {shownReach.suspended
                    ? 'すべての権限が停止されています。この人はいまこのクリニックの何も開けません。'
                    : ROLE_REACH[shownReach.role]}
                </p>
                <p className="muted small" style={{ margin: 0 }}>
                  登録日 {jaDate(shown.grants[0]?.grantedAt ?? null)}
                </p>
                <div className="grants__actions">
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={shown.userId === session.user.id}
                    onClick={() => startAccess(shown)}
                  >
                    権限を変更
                  </button>
                </div>
              </div>
            </div>

            <div className="editpanel__actions">
              <button className="btn btn--ghost btn--sm" type="button" onClick={() => startProfile(shown)}>
                プロフィールを編集
              </button>
              {shown.userId === session.user.id && (
                <span className="muted small">
                  自分自身の権限は変更できません。ほかの管理者に依頼してください。
                </span>
              )}
            </div>
          </>
        )}
      </Modal>

      {/* Adding a person, or another grant for one who already exists. */}
      <ModalForm
        open={open === 'new'}
        onClose={() => setOpen(null)}
        title="ユーザーを追加"
        caption="招待メールは送られません。このアドレスでGoogleログインした人が、この権限を使えるようになります。すでにいる人のアドレスなら、権限が追加されます。"
        submitLabel="追加する"
        onSubmit={addMember}
      >
        <Field
          label="メールアドレス"
          htmlFor="mb-email"
          required
          span="wide"
          hint="本人がGoogleログインに使うアドレスです。"
        >
          <input
            id="mb-email"
            type="email"
            required
            value={invite.email}
            maxLength={320}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
          />
        </Field>

        <Field label="権限" htmlFor="mb-role" hint={ROLE_REACH[invite.role]}>
          <Select
            id="mb-role"
            value={invite.role}
            onChange={(next) => setInvite({ ...invite, role: next as TenantRoleName })}
            options={roleOptions(canGrantClinicAdmin)}
          />
        </Field>

        {/*
          Optional, and blank is the normal answer: most people sign in with
          Google. It is here because a clinic without Workspace has no other way
          in, and the alternative was asking JMC to set one.
        */}
        <Field
          label="氏名"
          htmlFor="mb-name"
          span="wide"
          hint="新しくアカウントを作成する場合に使います。空欄ならメールアドレスから作られます。"
        >
          <input
            id="mb-name"
            value={invite.displayName}
            maxLength={200}
            placeholder="例：山田 花子"
            onChange={(e) => setInvite({ ...invite, displayName: e.target.value })}
          />
        </Field>

        <Field
          label="パスワード（任意）"
          htmlFor="mb-password"
          span="full"
          hint={`空欄ならGoogleログインのみになります。${PASSWORD_HINT}。あとからご本人がプロフィール画面で設定・変更できます。`}
        >
          <input
            id="mb-password"
            type="password"
            autoComplete="new-password"
            value={invite.password}
            minLength={MIN_PASSWORD_LENGTH}
            maxLength={200}
            placeholder={PASSWORD_HINT}
            onChange={(e) => setInvite({ ...invite, password: e.target.value })}
          />
        </Field>

        {invite.role !== 'CLINIC_ADMIN' && (
          <Field
            label="担当店舗"
            htmlFor="mb-store"
            hint="この店舗の記事と情報だけを扱えます。あとから変更できます。"
          >
            <Select
              id="mb-store"
              value={invite.storeId}
              onChange={(storeId) => setInvite({ ...invite, storeId })}
              options={[
                { value: '', label: '店舗を選択…' },
                ...manageStores.map((store) => ({ value: store.id, label: store.name })),
              ]}
            />
          </Field>
        )}

        {added && (
          <p className="notice field--full" role="status">
            {added}
          </p>
        )}
      </ModalForm>

      {/*
        The whole affiliation, in one dialog.

        Anything to anywhere: a store user becomes the clinic's admin, an admin
        becomes the person who covers two branches, and neither needs a revoke
        first. The server writes it as a set (PATCH /members/:id/affiliation), so
        what is on screen when this is saved is exactly what the person ends up
        holding - no leftover grant from what they were before.
      */}
      <ModalForm
        open={open === 'access'}
        onClose={() => setOpen(null)}
        title="権限を変更"
        caption={`${shown?.displayName || shown?.email || 'このユーザー'}が扱える範囲を決めます。保存すると、ログイン中のセッションは無効になります。`}
        submitLabel="この権限にする"
        wide
        onSubmit={saveAccess}
      >
        <Field
          label="権限"
          htmlFor="mb-access-role"
          span="wide"
          hint={ROLE_REACH[access.role]}
        >
          <Select
            id="mb-access-role"
            value={access.role}
            onChange={(next) => setAccess({ ...access, role: next as TenantRoleName })}
            options={roleOptions(canGrantClinicAdmin)}
          />
        </Field>

        {/*
          The branches, as checkboxes: the answer is usually "one of three", and
          the whole state is readable without opening a control. Same component
          as the JMC screen.
        */}
        {access.role !== 'CLINIC_ADMIN' && (
          <StoreChecklist
            idPrefix="mb-access-stores"
            stores={manageStores.map((store) => ({ id: store.id, name: store.name }))}
            value={access.storeIds}
            onChange={(storeIds) => setAccess({ ...access, storeIds })}
          />
        )}

        <div className="field field--full">
          <label className="checkline" htmlFor="mb-access-suspend">
            <input
              id="mb-access-suspend"
              type="checkbox"
              checked={access.suspend}
              onChange={(e) => setAccess({ ...access, suspend: e.target.checked })}
            />
            <span>いまはこのユーザーを停止する</span>
          </label>
          <span className="field__hint">
            停止すると、上で決めた範囲は残したままログインできなくなり、ログイン中のセッションは
            すべて即時に無効化されます。チェックを外せば同じ範囲で再開できます。
          </span>
        </div>
      </ModalForm>

      {/* The person themselves. The address is only ours to correct until they
          have signed in once - after that the provider rewrites it at login. */}
      <ModalForm
        open={open === 'profile'}
        onClose={() => setOpen(null)}
        title="プロフィールを編集"
        onSubmit={saveProfile}
      >
        <FieldGroup title="この人について" note="記事の署名には使われません" />
        <Field label="表示名" htmlFor="mb-name" required span="wide">
          <input
            id="mb-name"
            value={profileForm.displayName}
            maxLength={200}
            required
            onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
          />
        </Field>
        {shown?.claimed ? (
          <Field
            label="メールアドレス"
            htmlFor="mb-pemail"
            span="wide"
            hint="ログイン済みのため変更できません。本人のGoogleアカウントのアドレスです。"
          >
            <input id="mb-pemail" value={shown.email ?? ''} disabled readOnly />
          </Field>
        ) : (
          <Field
            label="メールアドレス"
            htmlFor="mb-pemail"
            span="wide"
            hint="このアドレスでGoogleログインした人が、このアカウントと権限を引き継ぎます。"
          >
            <input
              id="mb-pemail"
              type="email"
              value={profileForm.email}
              maxLength={320}
              required
              onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
            />
          </Field>
        )}
      </ModalForm>
    </Card>
  );
}
